import React, { useEffect, useRef, useState } from "react";
import Loader from "../components/Loader";
import { useNavigate } from "react-router-dom";
import { getOrdersByUserId } from "../Api";
import { cancelOwnOrder } from "../Api/order";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { fetchTrackingInfo } from "../Api";
import swal from 'sweetalert';
import Invoice from "../components/Invoice";

const loadScripts = () => {
  return new Promise((resolve, reject) => {
    const html2canvasScript = document.createElement('script');
    html2canvasScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    html2canvasScript.async = true;
    html2canvasScript.onload = () => {
      const jsPDFScript = document.createElement('script');
      jsPDFScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      jsPDFScript.async = true;
      jsPDFScript.onload = resolve;
      jsPDFScript.onerror = reject;
      document.head.appendChild(jsPDFScript);
    };
    html2canvasScript.onerror = reject;
    document.head.appendChild(html2canvasScript);
  });
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  padding: '16px',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #333',
  boxShadow: 24,
};

function MyOrder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(0);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const invoiceRef = useRef(null);
  const [loggedInUserData, setLoggedInUserData] = useState(
    JSON.parse(localStorage.getItem('user_data')) || {}
  );
  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  const handleOpen = async (order) => {
    setSelectedOrder(order);
    setOpen(true);
    if (order.status === 'completed') {
      try {
        setLoading(true);
        const data = await fetchTrackingInfo(order.id);
        setTrackingInfo(Array.isArray(data) ? data : []);
      } catch (error) {
        setTrackingInfo([]);
        swal('Error', 'Unable to fetch tracking information.', 'error');
      } finally {
        setLoading(false);
      }
    } else {
      setTrackingInfo(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOrder(null);
    setTrackingInfo(null);
  };

  useEffect(() => {
    let mounted = true;
    loadScripts()
      .then(() => {
        if (mounted) setScriptsLoaded(true);
      })
      .catch((error) => {
        console.error('Error loading scripts:', error);
        if (mounted) setScriptsLoaded(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user_data')) || {};
    if (userData.isSuperAdmin) {
      navigate('/dashboard');
    }
    fetchAllOrders()
    setLoggedInUserData(userData);
  }, [navigate]);

  const fetchAllOrders = async () => {
    if (!loggedInUserData?.id) {
      console.error('User data not found');
      setOrderItems([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await getOrdersByUserId(loggedInUserData.id);
      const orders = Array.isArray(response.data) ? response.data : [];
      setOrderItems(orders);
      localStorage.setItem('orderItems', JSON.stringify(orders));
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrderItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedOrderItems = localStorage.getItem('orderItems');
    if (storedOrderItems) {
      try {
        setOrderItems(JSON.parse(storedOrderItems));
      } catch (error) {
        console.error('Error parsing stored order items:', error);
        localStorage.removeItem('orderItems');
        fetchAllOrders();
      }
    } else {
      fetchAllOrders();
    }
  }, [loggedInUserData]);

  const getInvoice = async (id) => {
    if (isGeneratingInvoice || !scriptsLoaded) {
      alert('Please wait, invoice generation is in progress or scripts are not loaded.');
      return;
    }
    setIsGeneratingInvoice(true);
    setSelectedOrderId(id);
    try {
      const order = orderItems.find((o) => o.id === id);
      if (!order) {
        throw new Error('Order not found');
      }

      if (!window.html2canvas || !window.jspdf) {
        throw new Error('Required libraries (html2canvas or jsPDF) are not loaded');
      }

      const { jsPDF } = window.jspdf;

      await new Promise((resolve) => setTimeout(resolve, 100));

      const element = invoiceRef.current;
      if (!element) {
        throw new Error('Invoice element not found in the DOM');
      }

      const canvas = await window.html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`invoice_${id}.pdf`);
      alert('Invoice PDF download initiated. Check your downloads folder.');
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice. Please try again.');
    } finally {
      setIsGeneratingInvoice(false);
      setSelectedOrderId(0);
    }
  };

  const cancelOrder = async (orderId) => {
    const willCancel = await swal({
      title: "Are you sure?",
      text: "Do you want to cancel this order?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!willCancel) return;

    try {
      setLoading(true);

      await cancelOwnOrder(orderId);

      await fetchAllOrders();

      swal("Cancelled!", "Your order has been cancelled.", "success");
    } catch (error) {
      console.error("Error cancelling order:", error);
      // Surface the server's reason — e.g. an order that has already shipped cannot
      // be cancelled, and "Failed to cancel order" gives the customer nothing to act on.
      swal("Could not cancel", error.message || "Failed to cancel order.", "error");
    } finally {
      setLoading(false);
    }
  };

  const refreshOrder = async () => {
    await fetchAllOrders();
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="content-wrapper">
          <section className="my-order">
            <div className="orders-section">
              <div className="order-refresh-btn">
                <button onClick={refreshOrder}>Refresh Orders</button>
              </div>
              {orderItems.length === 0 ? (
                <div className="no-orders">
                  <h3>No orders placed</h3>
                  <p>Start shopping to see your orders here!</p>
                </div>
              ) : (
                <div>
                  {orderItems.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-grid">
                        <div>
                          <h4>Order ID</h4>
                          <p>{order.id}</p>
                        </div>
                        <div>
                          <h4>Date</h4>
                          <p>{new Date(order.date_created).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <h4>Status</h4>
                          <p className={`status-${order.status.toLowerCase()}`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </p>
                        </div>
                        <div>
                          <h4>Total</h4>
                          <p>{order.currency_symbol}{order.total}</p>
                        </div>
                      </div>
                      <div className="order-items">
                        <h4>Items</h4>
                        <ul>
                          {order.line_items.map((item) => (
                            <li key={item.id}>
                              {item.name} (x{item.quantity})
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="order-actions">
                        <button onClick={() => handleOpen(order)}>View</button>
                        {order.status !== "cancelled" && order.status !== "completed" && (
                          <button onClick={() => cancelOrder(order.id)}>
                            Cancel Order
                          </button>
                        )}
                        {order.status !== "cancelled" && order.status !== "completed" && (
                          <button
                            onClick={() => getInvoice(order.id)}
                            disabled={isGeneratingInvoice || !scriptsLoaded}
                          >
                            Download Invoice
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedOrderId !== 0 && (
              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <div ref={invoiceRef}>
                  <Invoice order={orderItems.find((o) => o.id === selectedOrderId)} />
                </div>
              </div>
            )}

            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={style} className="modal-content">
                <h3>Order Details</h3>
                <hr />
                {selectedOrder && (
                  <div>
                    <div className="modal-row">
                      <span>Order ID:</span>
                      <span>{selectedOrder.id}</span>
                    </div>
                    <div className="modal-row">
                      <span>Date:</span>
                      <span>{new Date(selectedOrder.date_created).toLocaleDateString()}</span>
                    </div>
                    <div className="modal-row">
                      <span>Status:</span>
                      <span className={`status-${selectedOrder.status.toLowerCase()}`}>
                        {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                      </span>
                    </div>
                    <div className="modal-row">
                      <span>Total:</span>
                      <span>{selectedOrder.currency_symbol}{selectedOrder.total}</span>
                    </div>
                    <div className="modal-items">
                      <span>Items:</span>
                      <ul>
                        {selectedOrder.line_items.map((item) => (
                          <li key={item.id}>
                            {item.name} (x{item.quantity})
                          </li>
                        ))}
                      </ul>
                    </div>
                    {trackingInfo && trackingInfo.length > 0 && (
                      <div className="modal-items">
                        <h4>Tracking Information</h4>
                        <div className="modal-row">
                          <span>Tracking Number:</span>
                          <span>{trackingInfo[0].tracking_number}</span>
                        </div>
                        <div className="modal-row">
                          <span>Carrier:</span>
                          <span>{trackingInfo[0].tracking_provider || trackingInfo[0].custom_tracking_provider || 'Unknown'}</span>
                        </div>
                        {trackingInfo[0].date_shipped && (
                          <div className="modal-row">
                            <span>Shipped On:</span>
                            <span>{new Date(trackingInfo[0].date_shipped).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      className="track-button"
                      onClick={() => {
                        if (trackingInfo && trackingInfo.length > 0) {
                          const tracking = trackingInfo[0];
                          const trackingUrl = tracking.custom_tracking_link || tracking.tracking_url;
                          if (trackingUrl) {
                            window.open(trackingUrl, '_blank');
                          } else {
                            swal('Info', 'No tracking URL available.', 'info');
                          }
                        } else {
                          swal('Info', 'No tracking information available.', 'info');
                        }
                      }}
                    >
                      Track Order
                    </button>
                  </div>
                )}
              </Box>
            </Modal>
          </section>
        </div>
      )}
    </>
  );
}

export default MyOrder;